import React from 'react';
import { checkPasswordStrength } from '../../utils/passwordStrength';

/**
 * Reusable password strength checker component.
 * Renders a checklist of 5 complexity rules with pass/fail indicators.
 * Used by ResetPassword.js and ProfileModal.jsx Security tab.
 *
 * Props:
 *   password {string} - The current raw (plaintext) password value to evaluate.
 *   className {string} - Optional extra Tailwind classes for the wrapper div.
 */
const RULES = [
    { key: 'minLength',  label: 'At least 12 characters' },
    { key: 'hasUpper',   label: 'Uppercase letter (A-Z)' },
    { key: 'hasLower',   label: 'Lowercase letter (a-z)' },
    { key: 'hasDigit',   label: 'Number (0-9)' },
    { key: 'hasSpecial', label: 'Special character (!@#$%^&* etc.)' },
];

const STRENGTH_LABELS = ['', 'Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong'];
const STRENGTH_COLORS = ['', '#ef4444', '#f97316', '#eab308', '#22c55e', '#16a34a'];

export default function PasswordStrengthChecker({ password = '', className = '' }) {
    if (!password) return null;

    const strength = checkPasswordStrength(password);
    const barColor = STRENGTH_COLORS[strength.score] || '#e5e7eb';
    const barLabel = STRENGTH_LABELS[strength.score] || '';

    return (
        <div className={`mt-2 space-y-2 ${className}`}>
            {/* Strength bar */}
            <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-gray-600 rounded-full overflow-hidden">
                    <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{
                            width: `${(strength.score / 5) * 100}%`,
                            backgroundColor: barColor,
                        }}
                    />
                </div>
                <span className="text-xs font-medium" style={{ color: barColor, minWidth: '70px', textAlign: 'right' }}>
                    {barLabel}
                </span>
            </div>

            {/* Checklist */}
            <ul className="space-y-1">
                {RULES.map(({ key, label }) => {
                    const passed = strength[key];
                    return (
                        <li key={key} className="flex items-center gap-1.5 text-xs">
                            <span className={passed ? 'text-green-400' : 'text-gray-500'}>
                                {passed ? '✓' : '○'}
                            </span>
                            <span className={passed ? 'text-green-400' : 'text-gray-400'}>
                                {label}
                            </span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
